export interface BandScore {
  criterion: string;
  code: 'TR' | 'CC' | 'LR' | 'GRA';
  score: number;
  descriptor: string;
  commentary: string;
}

export interface ErrorAnnotation {
  id: string;
  original: string;
  corrected: string;
  explanation: string;
  type: 'grammar' | 'vocabulary' | 'cohesion' | 'punctuation';
}

export interface AssessmentResult {
  overallBand: number;
  overallDescriptor: string;
  criteriaScores: BandScore[];
  annotatedEssayParagraphs: AnnotatedParagraph[];
  strengths: string[];
  weaknesses: string[];
  recommendations: Recommendation[];
  band9Outline: Band9Outline;
  band9Essay: string;
  wordCount: number;
  submittedAt: string;
  questionId: string;
}

export interface AnnotatedParagraph {
  id: string;
  segments: EssaySegment[];
}

export interface EssaySegment {
  id: string;
  text: string;
  isError: boolean;
  errorType?: 'grammar' | 'vocabulary' | 'cohesion' | 'punctuation';
  correction?: string;
  explanation?: string;
}

export interface Recommendation {
  id: string;
  priority: 'High' | 'Medium' | 'Low';
  criterion: string;
  action: string;
  detail: string;
}

export interface Band9Outline {
  essayType: string;
  thesisStatement: string;
  position: string;
  introduction: string;
  bodyParagraphs: { id: string; mainIdea: string; support: string }[];
  conclusion: string;
}

export const mockAssessment: AssessmentResult = {
  overallBand: 6.0,
  overallDescriptor: 'Competent User',
  wordCount: 287,
  submittedAt: '30 Jul 2026, 11:42',
  questionId: 'q-002',
  criteriaScores: [
    {
      criterion: 'Task Response',
      code: 'TR',
      score: 6.0,
      descriptor: 'Addresses all parts of the task, though some parts more fully than others.',
      commentary:
        'The response addresses the task prompt and presents a clear overall position, though the argument is not consistently developed throughout. The candidate acknowledges both views but the second perspective — rehabilitation and community-based interventions — receives considerably less attention than the first, creating an imbalance in task coverage. Supporting examples are present but remain somewhat generalised; the candidate references "many countries" and "studies" without specificity. The conclusion restates the position but does not add further insight. To reach Band 7, the candidate must ensure all parts of the task receive equal and thorough development, and that each supporting example is concrete and relevant.',
    },
    {
      criterion: 'Coherence & Cohesion',
      code: 'CC',
      score: 6.0,
      descriptor: 'Arranges information coherently with clear overall progression.',
      commentary:
        'The essay is organised into four paragraphs with a recognisable structure: introduction, two body paragraphs, and a conclusion. Paragraph development is generally adequate, though body paragraph two lacks a clear topic sentence and shifts between ideas without smooth transitions. Cohesive devices are used but are sometimes mechanical and repetitive — "Furthermore," "Moreover," and "In addition" appear in close succession, which creates a list-like rather than flowing argument. Reference chains are present but pronoun referencing occasionally creates ambiguity (e.g., "this" in paragraph three lacks a clear antecedent). To improve, the candidate should vary linking devices and ensure each paragraph opens with a clear, controlling idea.',
    },
    {
      criterion: 'Lexical Resource',
      code: 'LR',
      score: 5.5,
      descriptor: 'Uses a limited range of vocabulary; makes noticeable errors in word choice.',
      commentary:
        'The candidate demonstrates a basic but limited vocabulary range. High-frequency items predominate — "important," "good," "bad," "people," and "things" appear repeatedly without variation or paraphrase. There are several collocation errors ("do crimes," "make damage") and imprecise word choices ("criminal people" instead of "offenders" or "convicted individuals"). Topic-specific vocabulary related to criminal justice — recidivism, deterrence, rehabilitation, custodial sentence — is largely absent, which significantly restricts the ceiling score in this criterion. Spelling is generally accurate, though "governement" and "seperate" appear. Reaching Band 6.5 requires consistent use of less common vocabulary with appropriate collocation.',
    },
    {
      criterion: 'Grammatical Range & Accuracy',
      code: 'GRA',
      score: 6.5,
      descriptor: 'Uses a mix of simple and complex sentence forms; makes some errors.',
      commentary:
        'This is the candidate\'s strongest criterion. There is a reasonable range of sentence structures, including relative clauses, conditional sentences, and passive constructions, though complex structures are not always used with full accuracy. The most frequent error patterns involve article usage ("the government" used inconsistently), subject-verb agreement in complex noun phrases, and tense inconsistency between paragraphs. Run-on sentences appear twice in body paragraph two. Overall, grammatical errors are present but do not impede communication. To reach Band 7, the candidate should aim to use a wider variety of complex structures with greater accuracy, paying particular attention to article usage and tense consistency.',
    },
  ],
  annotatedEssayParagraphs: [
    {
      id: 'para-1',
      segments: [
        {
          id: 'seg-1-1',
          text: 'Nowadays, crime is a major problem in many countries and ',
          isError: false,
        },
        {
          id: 'seg-1-2',
          text: 'the government',
          isError: true,
          errorType: 'grammar',
          correction: 'governments',
          explanation: 'The plural form "governments" is required here as the sentence refers to multiple countries. Using the definite article "the" with a singular noun is incorrect in this generalising context.',
        },
        {
          id: 'seg-1-3',
          text: ' are always looking for effective solutions. Some people believe that longer prison sentences are the best way to ',
          isError: false,
        },
        {
          id: 'seg-1-4',
          text: 'reduce the criminal activities',
          isError: true,
          errorType: 'vocabulary',
          correction: 'reduce crime',
          explanation: '"Reduce the criminal activities" is an unnatural collocation. The idiomatic expression is simply "reduce crime" or "reduce criminal behaviour."',
        },
        {
          id: 'seg-1-5',
          text: ', while others think that alternative approaches are more effective. In this essay, I will discuss both perspectives and give my opinion.',
          isError: false,
        },
      ],
    },
    {
      id: 'para-2',
      segments: [
        {
          id: 'seg-2-1',
          text: 'On one hand, longer prison sentences can be a strong deterrent for ',
          isError: false,
        },
        {
          id: 'seg-2-2',
          text: 'criminal people',
          isError: true,
          errorType: 'vocabulary',
          correction: 'potential offenders',
          explanation: '"Criminal people" is an imprecise and informal expression. The appropriate academic term is "offenders," "potential offenders," or "would-be criminals."',
        },
        {
          id: 'seg-2-3',
          text: '. When people know that they will spend many years in prison, they may think twice before ',
          isError: false,
        },
        {
          id: 'seg-2-4',
          text: 'do crimes',
          isError: true,
          errorType: 'grammar',
          correction: 'committing crimes',
          explanation: 'The verb "do" does not collocate with "crimes." The correct collocation is "commit a crime." Additionally, the gerund form "committing" is required after "before."',
        },
        {
          id: 'seg-2-5',
          text: '. Furthermore, keeping offenders in prison for longer periods means they cannot ',
          isError: false,
        },
        {
          id: 'seg-2-6',
          text: 'make damage',
          isError: true,
          errorType: 'vocabulary',
          correction: 'cause harm',
          explanation: '"Make damage" is a collocation error. The correct expressions are "cause harm," "cause damage," or "inflict harm."',
        },
        {
          id: 'seg-2-7',
          text: ' to society during their incarceration. ',
          isError: false,
        },
        {
          id: 'seg-2-8',
          text: 'However, some studies shows',
          isError: true,
          errorType: 'grammar',
          correction: 'However, some studies show',
          explanation: 'Subject-verb agreement error. The plural subject "studies" requires the base form of the verb "show," not the third-person singular "shows."',
        },
        {
          id: 'seg-2-9',
          text: ' that long prison sentences do not always stop people from reoffending after release.',
          isError: false,
        },
      ],
    },
    {
      id: 'para-3',
      segments: [
        {
          id: 'seg-3-1',
          text: 'On the other hand, there are ',
          isError: false,
        },
        {
          id: 'seg-3-2',
          text: 'many other good',
          isError: true,
          errorType: 'vocabulary',
          correction: 'several more effective',
          explanation: '"Many other good" is vague and informal. In academic writing, use precise qualifiers such as "several more effective" or "a number of arguably superior."',
        },
        {
          id: 'seg-3-3',
          text: ' ways to reduce crime. Education and rehabilitation programmes can help offenders ',
          isError: false,
        },
        {
          id: 'seg-3-4',
          text: 'to become a useful member',
          isError: true,
          errorType: 'grammar',
          correction: 'to become useful members',
          explanation: 'Number agreement error. "Offenders" is plural, so the complement must also be plural: "useful members of society."',
        },
        {
          id: 'seg-3-5',
          text: ' of society. Moreover, community service can be very ',
          isError: false,
        },
        {
          id: 'seg-3-6',
          text: 'beneficially',
          isError: true,
          errorType: 'vocabulary',
          correction: 'beneficial',
          explanation: 'Word form error. "Beneficially" is an adverb; the adjective "beneficial" is required here as a predicative complement after "be."',
        },
        {
          id: 'seg-3-7',
          text: ' because it allows offenders to give back to the community and learn important skills. ',
          isError: false,
        },
        {
          id: 'seg-3-8',
          text: 'This',
          isError: true,
          errorType: 'cohesion',
          correction: 'This rehabilitative approach',
          explanation: 'Vague pronoun reference. "This" lacks a clear antecedent. Replace with a specific noun phrase such as "This rehabilitative approach" to maintain cohesive clarity.',
        },
        {
          id: 'seg-3-9',
          text: ' is especially important for young offenders who have the potential to change their behaviour.',
          isError: false,
        },
      ],
    },
    {
      id: 'para-4',
      segments: [
        {
          id: 'seg-4-1',
          text: 'In conclusion, while longer prison sentences can ',
          isError: false,
        },
        {
          id: 'seg-4-2',
          text: 'deter some criminal activities',
          isError: true,
          errorType: 'vocabulary',
          correction: 'deter some criminal behaviour',
          explanation: '"Deter criminal activities" is an unnatural collocation. "Deter criminal behaviour" or "deter crime" are the idiomatic forms.',
        },
        {
          id: 'seg-4-3',
          text: ', I believe that ',
          isError: false,
        },
        {
          id: 'seg-4-4',
          text: 'a combination of punitive and rehabilitative measures is more effective then',
          isError: true,
          errorType: 'grammar',
          correction: 'a combination of punitive and rehabilitative measures is more effective than',
          explanation: 'Spelling/grammar error. "Then" is a temporal adverb; the comparative conjunction required here is "than."',
        },
        {
          id: 'seg-4-5',
          text: ' relying on harsh sentences alone. ',
          isError: false,
        },
        {
          id: 'seg-4-6',
          text: 'The governement',
          isError: true,
          errorType: 'grammar',
          correction: 'Governments',
          explanation: 'Spelling error: "governement" should be "government." Additionally, the generalising context requires the plural "Governments" without the definite article.',
        },
        {
          id: 'seg-4-7',
          text: ' should invest in both approaches to create a safer and more just society.',
          isError: false,
        },
      ],
    },
  ],
  strengths: [
    'Clear overall position stated in the introduction and maintained throughout the essay.',
    'Reasonable paragraph structure with a recognisable four-part essay format (introduction, two body paragraphs, conclusion).',
    'Strongest criterion is Grammatical Range & Accuracy (6.5), demonstrating an ability to construct complex sentences including relative clauses and conditional structures.',
    'The argument in body paragraph one is logically developed with a cause-and-effect reasoning chain.',
    'Appropriate academic register is maintained overall, with no overtly informal language.',
  ],
  weaknesses: [
    'Lexical Resource is the weakest criterion (5.5), limited by over-reliance on high-frequency vocabulary and absence of topic-specific criminal justice terminology (recidivism, deterrence, custodial sentence, rehabilitation).',
    'Multiple collocation errors ("do crimes," "make damage") signal limited awareness of idiomatic academic English.',
    'Body paragraph two lacks a clear topic sentence, weakening the coherence of the argument at the paragraph level.',
    'The second view (alternative approaches) is underdeveloped relative to the first, creating task coverage imbalance that limits the Task Response score.',
    'Pronoun reference ambiguity ("This" in paragraph three) disrupts the cohesive flow of the argument.',
    'Vague generalisation in supporting examples ("studies show," "many countries") reduces the persuasive force of the argument.',
  ],
  recommendations: [
    {
      id: 'rec-001',
      priority: 'High',
      criterion: 'Lexical Resource',
      action: 'Build topic-specific criminal justice vocabulary',
      detail:
        'Actively learn and practise using high-level vocabulary relevant to crime and justice: recidivism, deterrence, incarceration, custodial sentence, rehabilitation, reintegration, restorative justice, punitive measures. Aim to use at least 3–4 domain-specific terms in every essay on this topic.',
    },
    {
      id: 'rec-002',
      priority: 'High',
      criterion: 'Lexical Resource',
      action: 'Eliminate collocation errors through targeted practice',
      detail:
        'Study common verb-noun collocations for crime topics: "commit a crime," "impose a sentence," "deter criminal behaviour," "cause harm," "address the root causes." Use a collocation dictionary (e.g., Oxford Collocations Dictionary) to check unfamiliar combinations before using them.',
    },
    {
      id: 'rec-003',
      priority: 'High',
      criterion: 'Task Response',
      action: 'Develop all parts of the task with equal depth',
      detail:
        'For Discussion essays, ensure both views receive equal word allocation and development. Each body paragraph should contain: a topic sentence stating the view, at least two supporting points, one specific example or piece of evidence, and a linking sentence connecting to the next paragraph.',
    },
    {
      id: 'rec-004',
      priority: 'Medium',
      criterion: 'Coherence & Cohesion',
      action: 'Vary cohesive devices and avoid mechanical linking',
      detail:
        'Replace the repetitive use of "Furthermore / Moreover / In addition" with a wider range of discourse markers: "By contrast," "Consequently," "It follows that," "This notwithstanding," "A more persuasive argument, however, holds that." Each paragraph should use no more than one of these additive linkers.',
    },
    {
      id: 'rec-005',
      priority: 'Medium',
      criterion: 'Coherence & Cohesion',
      action: 'Open every body paragraph with a clear topic sentence',
      detail:
        'A topic sentence should state the main idea of the paragraph in one sentence. Practise writing topic sentences that make a specific claim rather than a general statement. For example, instead of "There are many ways to reduce crime," write "Rehabilitation programmes address the root causes of criminal behaviour more effectively than punitive measures."',
    },
    {
      id: 'rec-006',
      priority: 'Medium',
      criterion: 'Grammatical Range & Accuracy',
      action: 'Review article usage rules for generalisation',
      detail:
        'A common error at Band 6–7 is inconsistent use of the definite article "the" with generalising nouns. Rule: when making a general statement about a category, use no article (e.g., "governments should invest," not "the government should invest" unless a specific government is meant). Practise rewriting sentences using this rule.',
    },
    {
      id: 'rec-007',
      priority: 'Low',
      criterion: 'Task Response',
      action: 'Use specific, verifiable examples rather than vague generalisations',
      detail:
        'Replace vague references ("studies show," "in many countries") with specific examples: named countries (Norway\'s rehabilitation model), named research findings, or clearly hypothetical scenarios introduced with "Consider a situation where..." This significantly strengthens the persuasive force of the argument.',
    },
  ],
  band9Outline: {
    essayType: 'Discussion — Both Views + Own Opinion',
    thesisStatement:
      'While longer prison sentences may deter some potential offenders and temporarily incapacitate convicted criminals, a multi-pronged approach combining proportionate sentencing with evidence-based rehabilitation programmes is demonstrably more effective at reducing long-term reoffending rates.',
    position:
      'Partial agreement with longer sentences as a short-term measure, but strong advocacy for rehabilitation as the primary long-term strategy.',
    introduction:
      'Contextualise the persistent challenge of criminal recidivism; acknowledge the intuitive appeal of harsher sentencing; signal that the essay will examine both perspectives before arguing that rehabilitation offers a more sustainable solution.',
    bodyParagraphs: [
      {
        id: 'bp-001',
        mainIdea: 'The case for longer prison sentences as a deterrent and incapacitation tool',
        support:
          'Deterrence theory (potential offenders calculate cost-benefit); incapacitation removes offenders from society during sentence; public safety and victim justice arguments; cite jurisdictions with mandatory minimum sentencing policies.',
      },
      {
        id: 'bp-002',
        mainIdea: 'The limitations of punitive sentencing and the evidence base for rehabilitation',
        support:
          'High recidivism rates in countries with long sentences (USA, UK); Norway\'s rehabilitation model — 20% reoffending rate vs UK\'s 45%; education, vocational training, and cognitive-behavioural therapy programmes; cost-effectiveness argument (rehabilitation vs. incarceration costs); addresses root causes rather than symptoms.',
      },
    ],
    conclusion:
      'Restate position: longer sentences alone are an insufficient and costly response; advocate for a sentencing framework that combines proportionate custodial terms with mandatory rehabilitation, drawing on the Scandinavian model as a proven alternative.',
  },
  band9Essay: `The question of how best to reduce crime has long divided policymakers, criminologists, and the public. While proponents of harsher sentencing argue that lengthier custodial terms serve as a powerful deterrent, a compelling body of evidence suggests that rehabilitation-centred approaches yield significantly more durable reductions in criminal behaviour. This essay will examine both perspectives before arguing that a balanced framework — one that combines proportionate sentencing with structured rehabilitation — represents the most effective strategy.

Those who advocate longer prison sentences advance two principal arguments. First, extended custodial terms are said to deter potential offenders by increasing the perceived cost of criminal behaviour; when the prospect of a decade in prison outweighs the anticipated benefit of a crime, rational actors are less likely to offend. Second, incarceration removes convicted individuals from society for the duration of their sentence, providing a period during which they cannot cause further harm to the public. These arguments carry genuine weight, particularly for serious and violent offences where public safety must be the paramount consideration.

However, the limitations of a purely punitive approach are well-documented. Countries that rely heavily on lengthy custodial sentences — including the United States and the United Kingdom — continue to record reoffending rates of between 40 and 50 percent within two years of release, suggesting that imprisonment alone fails to address the underlying causes of criminal behaviour. By contrast, Norway, which prioritises rehabilitation through education, vocational training, and cognitive-behavioural therapy within its prison system, has achieved a reoffending rate of approximately 20 percent — the lowest in the developed world. This stark disparity indicates that treating incarceration as an opportunity for genuine rehabilitation, rather than mere punishment, produces measurably better long-term outcomes for both offenders and society.

In conclusion, while longer prison sentences may serve a legitimate incapacitative function in cases involving serious offences, they are insufficient as a standalone strategy for reducing crime. Governments would be better served by adopting a sentencing framework that pairs proportionate custodial terms with mandatory, evidence-based rehabilitation programmes — an approach that not only reduces reoffending but also produces a more cost-effective and socially just criminal justice system.`,
};